import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  FileText, ArrowLeft, Info, TrendingUp, TrendingDown, DollarSign
} from 'lucide-react';

export default function ExecutivoDRE() {
  const [loading, setLoading] = useState(true);
  const [dre, setDre] = useState(null);
  const [canonicalDre, setCanonicalDre] = useState(null); // V5.2 · canônico
  const [filtros, setFiltros] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear()
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    loadDRE();
  }, [filtros.mes, filtros.ano]);

  const loadDRE = async () => {
    setLoading(true);
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const [response, canonicalRes] = await Promise.all([
        api.get('/executivo/dre', { params: filtros }),
        useLegacy
          ? Promise.resolve({ data: null })
          : api.get('/finance/dre', { params: { year: filtros.ano } }).catch(() => ({ data: null })),
      ]);
      setDre(response.data);
      if (canonicalRes.data) {
        setCanonicalDre(canonicalRes.data);
        console.info('[dre] motor canônico', canonicalRes.data);
      }
    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const absValue = Math.abs(value || 0);
    const sign = value < 0 ? '-' : '';
    return `${sign}R$ ${absValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const meses = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
  ];

  const getLinhaStyle = (tipo) => {
    switch (tipo) {
      case 'receita':
        return 'text-green-700 font-medium';
      case 'deducao':
        return 'text-red-600';
      case 'custo':
        return 'text-red-600';
      case 'tributos':
        return 'text-purple-600';
      case 'subtotal':
        return 'font-semibold bg-gray-50';
      case 'subtotal_principal':
        return 'font-bold bg-green-50 text-green-800';
      case 'resultado':
        return 'font-bold bg-blue-50 text-blue-800';
      case 'resultado_final':
        return 'font-bold bg-purple-100 text-purple-900 text-lg';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="executivo-dre">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/executivo')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            DRE Gerencial
          </h1>
          <p className="text-muted-foreground">Demonstração do Resultado - Lucro Real Operacional</p>
        </div>
        <div className="flex gap-2">
          <Select 
            value={String(filtros.mes)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, mes: parseInt(v) }))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meses.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select 
            value={String(filtros.ano)} 
            onValueChange={(v) => setFiltros(prev => ({ ...prev, ano: parseInt(v) }))}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(ano => (
                <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* DRE */}
      <Card>
        <CardHeader>
          <CardTitle>
            Demonstração do Resultado - {meses.find(m => m.value === filtros.mes)?.label}/{filtros.ano}
          </CardTitle>
          <CardDescription>
            Clique no ícone de informação para ver a memória de cálculo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2">
                  <th className="text-left py-3 px-2 w-16">Código</th>
                  <th className="text-left py-3 px-2">Descrição</th>
                  <th className="text-right py-3 px-2">Valor (R$)</th>
                  <th className="text-right py-3 px-2 w-24">% Receita</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {dre?.linhas?.map((linha, index) => (
                  <tr key={index} className={`border-b ${getLinhaStyle(linha.tipo)}`}>
                    <td className="py-3 px-2 text-sm text-muted-foreground">{linha.codigo}</td>
                    <td className="py-3 px-2">{linha.descricao}</td>
                    <td className="py-3 px-2 text-right font-mono">
                      {formatCurrency(linha.valor)}
                    </td>
                    <td className="py-3 px-2 text-right text-sm">
                      {linha.percentual_receita !== 0 && `${linha.percentual_receita}%`}
                    </td>
                    <td className="py-3 px-2">
                      {linha.memoria && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <Info className="w-3 h-3" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{linha.descricao}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium">Fórmula:</p>
                                <code className="text-sm">{linha.memoria.formula}</code>
                              </div>
                              {linha.memoria.componentes && (
                                <div className="p-3 bg-muted rounded-lg">
                                  <p className="text-sm font-medium mb-2">Componentes:</p>
                                  <ul className="text-sm space-y-1">
                                    {Object.entries(linha.memoria.componentes).map(([k, v]) => (
                                      <li key={k} className="flex justify-between">
                                        <span className="text-muted-foreground">{k}:</span>
                                        <span className="font-medium">{formatCurrency(v)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {linha.memoria.origem && (
                                <p className="text-xs text-muted-foreground">
                                  Origem: {linha.memoria.origem}
                                </p>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Resumo */}
      {dre?.resumo && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-green-50">
            <CardContent className="pt-4">
              <p className="text-sm text-green-600">Receita Real</p>
              <p className="text-xl font-bold text-green-700">
                {formatCurrency(dre.resumo.receita_real)}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50">
            <CardContent className="pt-4">
              <p className="text-sm text-blue-600">EBITDA</p>
              <p className="text-xl font-bold text-blue-700">
                {formatCurrency(dre.resumo.ebitda)}
              </p>
              <p className="text-xs text-blue-500">Margem: {dre.resumo.margem_ebitda}%</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50">
            <CardContent className="pt-4">
              <p className="text-sm text-purple-600">Lucro Líquido</p>
              <p className="text-xl font-bold text-purple-700">
                {formatCurrency(dre.resumo.lucro_liquido)}
              </p>
              <p className="text-xs text-purple-500">Margem: {dre.resumo.margem_liquida}%</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-50">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600">Total Custos</p>
              <p className="text-xl font-bold text-gray-700">
                {formatCurrency(dre.resumo.total_custos)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* V5.2 · Receita canônica (single source of truth: db.titles) */}
      {canonicalDre && (
        <Card className="bg-purple-50 border-purple-200" data-testid="dre-canonical-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-purple-700">
              Receita Anual Canônica · {canonicalDre.year}
            </CardTitle>
            <CardDescription className="text-xs">
              Fonte única: db.titles (títulos pagos) · sem drift entre módulos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-purple-800">
              {formatCurrency(canonicalDre.totais?.receita_total || 0)}
            </p>
            {canonicalDre.totais?.por_categoria && Object.keys(canonicalDre.totais.por_categoria).length > 0 && (
              <ul className="mt-3 space-y-1 text-sm">
                {Object.entries(canonicalDre.totais.por_categoria).map(([cat, v]) => (
                  <li key={cat} className="flex justify-between">
                    <span className="text-purple-700">{cat}</span>
                    <span className="font-mono">{formatCurrency(v)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {/* Link para DRE Anual */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => navigate('/executivo/dre-anual')}>
          Ver DRE Anual Consolidada
        </Button>
      </div>
    </div>
  );
}
